import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import { authFetch } from '../services/auth';

interface Profile {
  userId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  position?: string | null;
  roles: string[];
  isAdmin: boolean;
  avatarUrl?: string | null;
}

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Crop the chosen region of the source image into a square 256px JPEG data URL.
async function cropToDataUrl(src: string, area: Area): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return canvas.toDataURL('image/jpeg', 0.9);
}

export const ProfilePage: React.FC = () => {
  const { t } = useTranslation(['admin']);
  const [profile, setProfile] = useState<Profile | null>(null);

  // Editable personal fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [position, setPosition] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Avatar crop
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropArea, setCropArea] = useState<Area | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  // Password
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    authFetch('/api/admin/profile')
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Profile | null) => {
        if (!d) return;
        setProfile(d);
        setFirstName(d.firstName || '');
        setLastName(d.lastName || '');
        setPosition(d.position || '');
      })
      .catch(() => {});
  }, []);

  const displayName = [firstName, lastName].filter(Boolean).join(' ') || profile?.email || '';
  const initial = (displayName || '?').charAt(0).toUpperCase();

  const onPickFile = (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      alert(t('admin:profileAvatarTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropSrc(String(reader.result));
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => setCropArea(areaPixels), []);

  const saveAvatar = async () => {
    if (!cropSrc || !cropArea) return;
    setAvatarBusy(true);
    try {
      const dataUrl = await cropToDataUrl(cropSrc, cropArea);
      const res = await authFetch('/api/admin/profile/avatar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: 'image/jpeg', contentBase64: dataUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const { avatarUrl } = await res.json();
      setProfile((p) => (p ? { ...p, avatarUrl } : p));
      setCropSrc(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:profileAvatarError'));
    } finally {
      setAvatarBusy(false);
    }
  };

  const saveProfile = async () => {
    setProfileMsg(null);
    setSavingProfile(true);
    try {
      const res = await authFetch('/api/admin/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim(), position: position.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setProfile((p) => (p ? { ...p, firstName, lastName, position } : p));
      setProfileMsg({ kind: 'ok', text: t('admin:profileSaved') });
    } catch {
      setProfileMsg({ kind: 'err', text: t('admin:profileSaveError') });
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    setPwMsg(null);
    if (newPassword.length < 8) {
      setPwMsg({ kind: 'err', text: t('admin:profilePwTooShort') });
      return;
    }
    if (newPassword !== confirm) {
      setPwMsg({ kind: 'err', text: t('admin:profilePwMismatch') });
      return;
    }
    setPwBusy(true);
    try {
      const res = await authFetch('/api/identity/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldPassword, newPassword }),
      });
      if (res.ok) {
        setPwMsg({ kind: 'ok', text: t('admin:profilePwChanged') });
        setOldPassword('');
        setNewPassword('');
        setConfirm('');
      } else {
        const err = await res.json().catch(() => ({}));
        const invalid = (err.title || err.detail || '').toLowerCase().includes('invalid');
        setPwMsg({ kind: 'err', text: invalid ? t('admin:profilePwWrong') : err.detail || t('admin:profilePwError') });
      }
    } catch {
      setPwMsg({ kind: 'err', text: t('admin:profilePwError') });
    } finally {
      setPwBusy(false);
    }
  };

  const inputCls =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
  const btnPrimary =
    'rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50';

  return (
    <div className="admin-page od-modern">
      <div className="admin-header">
        <div>
          <h1>{t('admin:profileTitle')}</h1>
          <p className="admin-subtitle">{t('admin:profileSubtitle')}</p>
        </div>
      </div>

      <div className="mx-auto grid max-w-3xl grid-cols-1 gap-5 md:grid-cols-[260px_1fr]">
        {/* Avatar + identity */}
        <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-100 bg-white p-5">
          {profile?.avatarUrl ? (
            <img src={profile.avatarUrl} alt="" className="h-24 w-24 rounded-full object-cover" />
          ) : (
            <span className="flex h-24 w-24 items-center justify-center rounded-full bg-indigo-50 text-3xl font-semibold text-indigo-500">
              {initial}
            </span>
          )}
          <label className={`inline-flex cursor-pointer items-center rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-blue-700`}>
            {t('admin:profileChangePhoto')}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onPickFile(f);
                e.target.value = '';
              }}
            />
          </label>
          <div className="text-center">
            <div className="font-semibold text-slate-900">{displayName}</div>
            {position && <div className="text-sm text-slate-500">{position}</div>}
            <div className="break-all text-xs text-slate-400">{profile?.email}</div>
            {profile?.roles?.length ? <div className="mt-1 text-xs text-slate-400">{profile.roles.join(' · ')}</div> : null}
          </div>
        </div>

        <div className="flex flex-col gap-5">
          {/* Personal data */}
          <div className="rounded-xl border border-slate-100 bg-white p-5">
            <div className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">{t('admin:profilePersonal')}</div>
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm text-slate-600">
                  {t('admin:profileFirstName')}
                  <input className={inputCls} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </label>
                <label className="flex flex-col gap-1 text-sm text-slate-600">
                  {t('admin:profileLastName')}
                  <input className={inputCls} value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </label>
              </div>
              <label className="flex flex-col gap-1 text-sm text-slate-600">
                {t('admin:profilePosition')}
                <input className={inputCls} value={position} onChange={(e) => setPosition(e.target.value)} placeholder={t('admin:profilePositionPlaceholder')} />
              </label>
              {profileMsg && (
                <div className={`rounded-lg px-3 py-2 text-sm ${profileMsg.kind === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {profileMsg.text}
                </div>
              )}
              <button type="button" className={`self-start ${btnPrimary}`} disabled={savingProfile} onClick={saveProfile}>
                {savingProfile ? t('admin:profileSaving') : t('admin:profileSave')}
              </button>
            </div>
          </div>

          {/* Change password */}
          <div className="rounded-xl border border-slate-100 bg-white p-5">
            <div className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">{t('admin:profileChangePassword')}</div>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm text-slate-600">
                {t('admin:profileCurrentPassword')}
                <input type="password" className={inputCls} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} autoComplete="current-password" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-600">
                {t('admin:profileNewPassword')}
                <input type="password" className={inputCls} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-600">
                {t('admin:profileConfirmPassword')}
                <input type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
              </label>
              {pwMsg && (
                <div className={`rounded-lg px-3 py-2 text-sm ${pwMsg.kind === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {pwMsg.text}
                </div>
              )}
              <button type="button" className={`self-start ${btnPrimary}`} disabled={pwBusy || !oldPassword || !newPassword || !confirm} onClick={changePassword}>
                {pwBusy ? t('admin:profileSaving') : t('admin:profileSave')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Crop modal */}
      {cropSrc && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" onClick={() => !avatarBusy && setCropSrc(null)}>
          <div className="od-modern w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 text-sm font-semibold text-slate-700">{t('admin:profileCrop')}</div>
            <div className="relative h-64 w-full overflow-hidden rounded-lg bg-slate-900">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="mt-3 w-full"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50" disabled={avatarBusy} onClick={() => setCropSrc(null)}>
                {t('admin:profileCropCancel')}
              </button>
              <button type="button" className={btnPrimary} disabled={avatarBusy} onClick={saveAvatar}>
                {avatarBusy ? t('admin:profileUploading') : t('admin:profileCropConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
